/**
 * Group Detail Page
 *
 * Drill-down view for a single group.
 * Shows health metrics, members, and attendance trends.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DataTable from '../../components/founder-dashboard/DataTable';
import HealthBadge from '../../components/founder-dashboard/HealthBadge';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import FeatureHealthCard from '../../components/feature-analytics/FeatureHealthCard';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function GroupDetailPage() {
  const { currentEnv } = useEnvironment();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to
  const [featureHealth, setFeatureHealth] = useState(null);
  const [featureHealthLoading, setFeatureHealthLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getGroup(id);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[GroupDetailPage] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData :
                       errorData?.message || err.message || 'Failed to load data';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    setFeatureHealth(null);
    fetchData();
  }, [id, currentEnv]);

  // Fetch feature health data filtered by this group
  useEffect(() => {
    const loadFeatureHealth = async () => {
      if (!id) return;
      setFeatureHealthLoading(true);
      try {
        const response = await founderDashboardAPI.getFeatureSummary({ groupId: id });
        setFeatureHealth(response.data.summary);
      } catch (err) {
        console.error('[GroupDetailPage] Failed to load feature health:', err);
        // Don't set error state - feature health is supplemental
      } finally {
        setFeatureHealthLoading(false);
      }
    };
    loadFeatureHealth();
  }, [id, currentEnv]);

  const memberColumns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{value}</span>
          {row.role === 'leader' && (
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
              Leader
            </span>
          )}
          {row.isAtRisk && (
            <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
              At Risk
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'attendanceRate',
      header: 'Attendance',
      sortable: true,
      render: (value) => (value !== null ? `${(value * 100).toFixed(0)}%` : '-'),
    },
    {
      key: 'prayerCount',
      header: 'Prayers',
      sortable: true,
    },
    {
      key: 'joinedAt',
      header: 'Joined',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading group</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const group = data?.group || {};
  const metrics = data?.metrics || {};
  const members = data?.members || [];
  const attendanceTrend = data?.attendanceTrend || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          {loading ? (
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{group.name}</h2>
              <p className="text-gray-500 mt-1">
                {group.organizationName} &bull; {group.type}
              </p>
            </div>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Group Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">Meeting Day</p>
            <p className="text-lg font-semibold text-gray-900 mt-1 capitalize">
              {loading ? '-' : group.meetingDay || 'Not set'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Meeting Time</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {loading ? '-' : group.meetingTime || 'Not set'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Meeting Type</p>
            <p className="text-lg font-semibold text-gray-900 mt-1 capitalize">
              {loading ? '-' : group.meetingType || 'Not set'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Health Score</p>
            <div className="mt-1">
              {loading ? '-' : <HealthBadge score={metrics.healthScore} size="lg" />}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard title="Members" value={metrics.memberCount} loading={loading} />
        <KPICard title="Leaders" value={metrics.leaderCount} loading={loading} />
        <KPICard
          title="Attendance Rate"
          value={metrics.attendanceRate}
          format="percent"
          loading={loading}
        />
        <KPICard title="Prayers This Month" value={metrics.prayersThisMonth} loading={loading} />
        <KPICard title="Events This Month" value={metrics.eventsThisMonth} loading={loading} />
      </div>

      {/* Feature Health Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Feature Health</h3>

        {featureHealthLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : featureHealth ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureHealthCard
              feature="prayer"
              health={featureHealth.prayer?.health || 'yellow'}
              adoption={featureHealth.prayer?.adoption || 0}
              usageLabel={featureHealth.prayer?.usageLabel || '0/user'}
              effectiveness={featureHealth.prayer?.effectiveness || 0}
              organizationId={group.organizationId}
              campusId={group.campusId}
              groupId={id}
            />
            <FeatureHealthCard
              feature="attendance"
              health={featureHealth.attendance?.health || 'yellow'}
              adoption={featureHealth.attendance?.adoption || 0}
              usageLabel={featureHealth.attendance?.usageLabel || '0%'}
              effectivenessLabel={featureHealth.attendance?.effectivenessLabel || '0 at-risk'}
              organizationId={group.organizationId}
              campusId={group.campusId}
              groupId={id}
            />
            <FeatureHealthCard
              feature="events"
              health={featureHealth.events?.health || 'yellow'}
              adoption={featureHealth.events?.adoption || 0}
              usageLabel={featureHealth.events?.usageLabel || '0/group'}
              effectiveness={featureHealth.events?.effectiveness || 0}
              organizationId={group.organizationId}
              campusId={group.campusId}
              groupId={id}
            />
            <FeatureHealthCard
              feature="chat"
              health={featureHealth.chat?.health || 'yellow'}
              adoption={featureHealth.chat?.adoption || 0}
              usageLabel={featureHealth.chat?.usageLabel || '0/day'}
              effectiveness={featureHealth.chat?.effectiveness || 0}
              organizationId={group.organizationId}
              campusId={group.campusId}
              groupId={id}
            />
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Feature health data unavailable</p>
        )}
      </div>

      {/* Attendance Trend Chart */}
      {attendanceTrend.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Attendance Trend (Last 8 Weeks)
          </h3>
          <TrendChart
            data={attendanceTrend.map((t) => ({
              date: t.date,
              rate: t.rate * 100,
            }))}
            lines={[{ key: 'rate', name: 'Attendance %', color: '#3B82F6' }]}
            xAxisKey="date"
            height={250}
          />
        </div>
      )}

      {/* Members Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Members ({members.length})
        </h3>
        <DataTable
          data={members}
          columns={memberColumns}
          loading={loading}
          onRowClick={(member) => navigate(`/founder-dashboard/members/${member.id}`)}
          emptyMessage="No members found"
        />
      </div>
    </div>
  );
}
